import React, { useState, useEffect } from 'react';

export interface CalendarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** The currently selected date */
  selectedDate?: Date;
  /** Handler that fires when user selects a date */
  onChange?: (date: Date) => void;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Initial view date (defaults to current month if no selectedDate) */
  initialViewDate?: Date;
  /** Array of dates to highlight */
  highlightedDates?: Date[];
  /** Custom renderer for day cells */
  renderDay?: (date: Date, isSelected: boolean, isHighlighted: boolean) => React.ReactNode;
  /** Custom class name for the component */
  className?: string;
  /** Whether the calendar is disabled */
  disabled?: boolean;
  /** Enable compact view */
  compact?: boolean;
  /** Function to determine if date is disabled */
  isDateDisabled?: (date: Date) => boolean;
}

export const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      selectedDate,
      onChange,
      minDate,
      maxDate,
      initialViewDate,
      highlightedDates = [],
      renderDay,
      className = '',
      disabled = false,
      compact = false,
      isDateDisabled,
      ...props
    },
    ref
  ) => {
    // Use selectedDate or current date as initial view
    const [viewDate, setViewDate] = useState(
      initialViewDate || selectedDate || new Date()
    );

    // Update the viewDate if selectedDate changes
    useEffect(() => {
      if (selectedDate) {
        setViewDate(selectedDate);
      }
    }, [selectedDate]);

    // Get the first day of the month
    const firstDayOfMonth = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth(),
      1
    );

    // Get the last day of the month
    const lastDayOfMonth = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth() + 1,
      0
    );

    // Get the day of the week of the first day of the month (0-6, where 0 is Sunday)
    const firstDayOfWeek = firstDayOfMonth.getDay();
    
    // Go to previous month
    const handlePrevMonth = () => {
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    // Go to next month
    const handleNextMonth = () => {
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    // Check if a date is the same day as the selected date
    const isSameDay = (date1: Date, date2: Date) => {
      return (
        date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear()
      );
    };

    // Check if a date is in the array of highlighted dates
    const isHighlighted = (date: Date) => {
      return highlightedDates.some(highlightedDate => 
        isSameDay(date, highlightedDate)
      );
    };

    // Check if a date is disabled
    const isDisabled = (date: Date) => {
      if (disabled) return true;
      
      if (minDate && date < new Date(minDate.setHours(0,0,0,0))) return true;
      if (maxDate && date > new Date(maxDate.setHours(23,59,59,999))) return true;
      
      if (isDateDisabled && isDateDisabled(date)) return true;
      
      return false;
    };

    // Handle date selection
    const handleDateClick = (date: Date) => {
      if (isDisabled(date) || !onChange) return;
      
      onChange(date);
    };

    // Generate calendar days
    const renderCalendarDays = () => {
      const days = [];
      const daysInMonth = lastDayOfMonth.getDate();
      
      // Add days from previous month to fill the first week
      const prevMonthLastDay = new Date(
        viewDate.getFullYear(),
        viewDate.getMonth(),
        0
      ).getDate();
      
      for (let i = 0; i < firstDayOfWeek; i++) {
        const date = new Date(
          viewDate.getFullYear(),
          viewDate.getMonth() - 1,
          prevMonthLastDay - firstDayOfWeek + i + 1
        );
        
        days.push(
          <button
            key={`prev-${i}`}
            type="button"
            disabled={true}
            className="text-gray-400 dark:text-gray-400 p-2 text-center"
          >
            {date.getDate()}
          </button>
        );
      }
      
      // Current month days
      for (let i = 1; i <= daysInMonth; i++) {
        const date = new Date(
          viewDate.getFullYear(),
          viewDate.getMonth(),
          i
        );
        
        const isCurrentSelected = selectedDate && isSameDay(date, selectedDate);
        const isDateHighlighted = isHighlighted(date);
        const dateDisabled = isDisabled(date);
        
        const dayContent = renderDay ? (
          renderDay(date, Boolean(isCurrentSelected), Boolean(isDateHighlighted))
        ) : (
          i
        );
        
        days.push(
          <button
            key={i}
            type="button"
            onClick={() => handleDateClick(date)}
            disabled={dateDisabled}
            className={`
              w-full aspect-square flex items-center justify-center
              ${isCurrentSelected ? 'bg-blue-500 dark:bg-blue-700 text-white font-semibold rounded-full' : ''}
              ${isDateHighlighted && !isCurrentSelected ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full' : ''}
              ${dateDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}
              ${!isCurrentSelected && !isDateHighlighted ? 'text-gray-800 dark:text-gray-200' : ''}
              text-center p-2 transition-colors
            `}
          >
            {dayContent}
          </button>
        );
      }
      
      // Add days from next month to fill the last week
      const remainingCells = 42 - days.length; // 6 rows x 7 days
      
      for (let i = 1; i <= remainingCells; i++) {
        const date = new Date(
          viewDate.getFullYear(),
          viewDate.getMonth() + 1,
          i
        );
        
        days.push(
          <button
            key={`next-${i}`}
            type="button"
            disabled={true}
            className="text-gray-400 dark:text-gray-400 p-2 text-center"
          >
            {date.getDate()}
          </button>
        );
      }
      
      return days;
    };

    // Week day labels
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const compactWeekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    return (
      <div
        ref={ref}
        className={`
          w-full
          bg-white dark:bg-gray-800
          border border-gray-200 dark:border-gray-700
          rounded-lg
          ${className}
        `}
        {...props}
      >
        {/* Calendar header with month navigation */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handlePrevMonth}
            disabled={disabled}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            &lt;
          </button>
          
          <h2 className="text-gray-800 dark:text-gray-200 font-medium">
            {viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          
          <button
            type="button"
            onClick={handleNextMonth}
            disabled={disabled}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            &gt;
          </button>
        </div>
        
        {/* Week days header */}
        <div className="grid grid-cols-7 gap-1 p-2 border-b border-gray-200 dark:border-gray-700">
          {(compact ? compactWeekDays : weekDays).map((day) => (
            <div
              key={day}
              className="text-gray-500 dark:text-gray-400 text-center font-medium"
            >
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1 p-2">
          {renderCalendarDays()}
        </div>
      </div>
    );
  }
);

Calendar.displayName = 'Calendar';

export default Calendar;